import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { MAX_SECTION_SEATS, MAX_SECTIONS, type ClassFormValues } from '../schemas/class.schema';
import { nextSectionName, toAmount, totalIntake } from '../utils';

export interface SectionsFieldProps {
  form: UseFormReturn<ClassFormValues>;
}

/** The column widths, shared by the header and every row so the two line up. */
const ROW_GRID =
  'sm:grid-cols-[3.5rem_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_3.5rem_2.25rem]';

/**
 * The sections a class is split into, each with its own intake.
 *
 * One row per section rather than a card each: a class runs up to ten of them
 * and they are read as a set — "is 8-C smaller than the rest?" is a question you
 * answer by running your eye down a column, which a grid of blocks does not
 * give you. The columns are captioned once at the top instead of every field
 * carrying its own label, so ten sections are ten lines rather than ten panels.
 *
 * Capacity is held per section rather than per class because that is the number
 * the office actually works to — a class of 105 means nothing when admissions
 * are turning a parent away from 8-B.
 */
export function SectionsField({ form }: SectionsFieldProps) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;
  const { fields, append, remove, move } = useFieldArray({ control, name: 'sections' });

  const sections = watch('sections') ?? [];
  const isFull = fields.length >= MAX_SECTIONS;
  // Zod reports "at least one" / "at most ten" against the array itself.
  const listError = errors.sections?.root?.message ?? errors.sections?.message;

  const intake = totalIntake(sections);
  /** "A (35)" per named section — the shorthand the roster prints. */
  const summary = sections
    .filter((section) => section.name.trim() !== '')
    .map((section) => `${section.name.trim()} (${toAmount(section.maxSeats) || '–'})`);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="lbl">Sections</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-3">
            {fields.length} of {MAX_SECTIONS}
          </span>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            disabled={isFull}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2 rounded-xl text-xs shadow-xs inline-flex items-center gap-1.5 cursor-pointer border-0"
            onClick={() => {
              append({ name: nextSectionName(sections), minSeats: '1', maxSeats: '' });
              form.clearErrors('sections');
            }}
          >
            Add section
          </Button>
        </div>
      </div>

      {listError && (
        <p role="alert" className="text-xs text-crit">
          {listError}
        </p>
      )}

      <div className="overflow-hidden rounded-2xl border border-line">
        {/* Captioned once, so the fields below can go without labels of their
            own. Held back on a phone, where each cell captions itself. */}
        <div
          className={cn(
            'hidden border-b border-line bg-surface-2 px-3 py-2 sm:grid sm:items-center sm:gap-3',
            ROW_GRID,
          )}
        >
          <span className="lbl text-[11px]">Order</span>
          <span className="lbl text-[11px]">Section name</span>
          <span className="lbl text-[11px]">Min seats</span>
          <span className="lbl text-[11px]">Max seats</span>
          <span className="lbl text-[11px] text-right">Seats</span>
          <span className="sr-only">Remove</span>
        </div>

        <div className="divide-y divide-line">
          {fields.map((field, index) => {
            const seats = toAmount(sections[index]?.maxSeats ?? '');

            return (
              <div
                key={field.id}
                className={cn(
                  'grid grid-cols-1 gap-3 px-3 py-3 sm:items-start sm:gap-3',
                  ROW_GRID,
                )}
              >
                {/* Up and down rather than a drag handle: a keyboard reaches
                    these, a pointer-only drag does not, and the order this sets
                    is the order the register and the timetable read back. */}
                <div className="hidden items-center gap-0.5 sm:mt-2.5 sm:flex">
                  <span className="w-4 font-mono text-sm font-bold text-ink-3">{index + 1}</span>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label={`Move section ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      className="cursor-pointer rounded-t-md px-1 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                      <ChevronUp className="h-3 w-3" aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move section ${index + 1} down`}
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      className="cursor-pointer rounded-b-md px-1 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                      <ChevronDown className="h-3 w-3" aria-hidden />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="lbl text-[11px] sm:hidden">Section {index + 1} name</span>
                  <Input
                    label={`Section ${index + 1} name`}
                    hideLabel
                    placeholder="A"
                    autoComplete="off"
                    error={errors.sections?.[index]?.name?.message}
                    {...register(`sections.${index}.name` as const)}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="lbl text-[11px] sm:hidden">Min seats</span>
                  <Input
                    label={`Section ${index + 1} minimum seats`}
                    hideLabel
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_SECTION_SEATS}
                    placeholder="20"
                    className="font-mono"
                    error={errors.sections?.[index]?.minSeats?.message}
                    {...register(`sections.${index}.minSeats` as const)}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="lbl text-[11px] sm:hidden">Max seats</span>
                  <Input
                    label={`Section ${index + 1} maximum seats`}
                    hideLabel
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_SECTION_SEATS}
                    placeholder="35"
                    className="font-mono"
                    error={errors.sections?.[index]?.maxSeats?.message}
                    {...register(`sections.${index}.maxSeats` as const)}
                  />
                </div>

                {/* What this row contributes to the intake, so the sum at the
                    foot can be checked a line at a time. */}
                <span className="hidden text-right font-mono text-sm font-bold text-ink sm:mt-3 sm:block">
                  {seats || '–'}
                </span>

                <div className="flex justify-end sm:mt-1.5">
                  {fields.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 text-ink-3 hover:bg-crit-bg hover:text-crit"
                      aria-label={`Remove section ${index + 1}`}
                      onClick={() => {
                        remove(index);
                        form.clearErrors('sections');
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface-2 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {summary.length === 0 ? (
              <span className="text-xs text-ink-3">Name a section to start</span>
            ) : (
              summary.map((chip, index) => (
                <span
                  key={index}
                  className="rounded-full border border-line-strong bg-surface px-2 py-0.5 font-mono text-[11px] font-bold text-ink-2"
                >
                  {chip}
                </span>
              ))
            )}
          </div>
          <span className="text-xs font-bold text-ink">
            {intake.toLocaleString('en-IN')} seats across {fields.length}{' '}
            {fields.length === 1 ? 'section' : 'sections'}
          </span>
        </div>
      </div>

      <p className="text-xs text-ink-3">
        A class may have up to {MAX_SECTIONS} sections, listed in the order you arrange them here. The minimum is the smallest strength worth
        running a section at; the maximum is the cap admissions works to, and the one the intake
        total counts.
      </p>
    </div>
  );
}
